import { Button, Card, Descriptions, Popconfirm, Space, Spin, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OwnerType } from '@never2busy/shared';
import { activitiesApi } from '../../../api/endpoints/activities';
import { PageHeader } from '../../../components/common/PageHeader';
import { AttachmentsList } from '../../../components/common/AttachmentsList';
import { KeywordsManager } from '../../../components/common/KeywordsManager';

export function ActivityDetailPage(): JSX.Element {
  const { id } = useParams();
  const activityId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['activity', activityId],
    queryFn: () => activitiesApi.findOne(activityId),
  });

  const removeMutation = useMutation({
    mutationFn: () => activitiesApi.remove(activityId),
    onSuccess: () => {
      message.success('Actividad eliminada');
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      navigate('/actividades');
    },
    onError: () => message.error('No se pudo eliminar la actividad'),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!data) return <div>Actividad no encontrada</div>;

  const title = `${data.type ?? 'Actividad'} #${data.id}`;

  return (
    <>
      <PageHeader
        title={title}
        crumbs={[{ title: 'Actividades', to: '/actividades' }, { title }]}
        extra={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/actividades/${data.id}/editar`)}
            >
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar esta actividad?"
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={removeMutation.isPending}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Tipo">
            {data.type ? <Tag>{data.type}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Estado">
            {data.closed ? <Tag color="green">Cerrada</Tag> : <Tag color="processing">Abierta</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Cuenta">
            {data.account ? (
              <Link to={`/cuentas/${data.account.id}`}>{data.account.name}</Link>
            ) : (
              data.accountNameSnapshot ?? '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Contacto">
            {data.person ? (
              <Link to={`/contactos/${data.person.id}`}>
                {data.person.firstName ?? ''} {data.person.lastName}
              </Link>
            ) : (
              data.personNameSnapshot ?? '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Inicio">
            {data.startDate ? dayjs(data.startDate).format('DD/MM/YYYY HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Fin">
            {data.endDate ? dayjs(data.endDate).format('DD/MM/YYYY HH:mm') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Asignada a">
            {data.assignedTo
              ? `${data.assignedTo.firstName} ${data.assignedTo.lastName}`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Creada">
            {dayjs(data.createdAt).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        </Descriptions>

        {data.notes && (
          <div style={{ marginTop: 16 }}>
            <strong>Notas:</strong>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{data.notes}</div>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <strong>Etiquetas:</strong>
          <div style={{ marginTop: 8 }}>
            <KeywordsManager ownerType={OwnerType.ACTIVITY} ownerId={data.id} />
          </div>
        </div>
      </Card>

      <Card
        title={
          <Space>
            <PaperClipOutlined />
            <span>Adjuntos</span>
          </Space>
        }
      >
        <AttachmentsList ownerType={OwnerType.ACTIVITY} ownerId={data.id} />
      </Card>
    </>
  );
}
